import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import {
    adminUserService,
    AdminUser,
    GetAdminUsersParams
} from "../../services/admin.user.service";
import { adminRoleService } from "../../services/admin.role.service";
import { errorService } from "../../services/error.service";
import CreateUserModal from "../../components/admin/CreateUserModal";
import UpdateUserModal from "../../components/admin/UpdateUserModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUsers,
    faSearch,
    faPlus,
    faCircleNotch,
    faTriangleExclamation,
    faUserSlash,
    faUser,
    faEdit
} from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/pages/AdminUsersPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";
import Swal from "sweetalert2";




const AdminUsersPage: React.FC = () => {
    const navigate = useNavigate();
    const { adminData } = useAdminAuth();

    useEffect(() => {
        if (!permissionCheckerService.checkPermission(adminData, "USERS", "READ")) {
            navigate("/admin/dashboard", { replace: true });
        }
    }, [adminData, navigate]);

    const [users, setUsers] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Filter states
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [roleFilter, setRoleFilter] = useState<string>("");
    const [roles, setRoles] = useState<{ id: number; roleName: string }[]>([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);



    const fetchUsers = async (page: number, size: number) => {
        try {
            setLoading(true);
            setError(null);

            const params: GetAdminUsersParams = {
                page: page,
                pageSize: size,
                status: statusFilter ? parseInt(statusFilter) : undefined,
                idRole: roleFilter ? parseInt(roleFilter) : undefined,
                SearchString: searchText || undefined,
            };

            const response = await adminUserService.getUsers(params);

            if (response.statusCode === 200) {
                setUsers(response.data);
                setTotalRows(response.pagination.totalCount);
            } else {
                setError(errorService.getDisplayMessage(response));
            }
        } catch (err: any) {
            console.error("Error fetching admin users:", err);
            setError(errorService.getDisplayMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await adminRoleService.getRolesSelect();
            if (response.statusCode === 200) {
                setRoles(response.data);
            }
        } catch (err) {
            console.error("Error fetching roles for filter:", err);
        }
    };

    useEffect(() => {
        fetchUsers(1, perPage);
        fetchRoles();
    }, []);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchUsers(page, perPage);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        fetchUsers(page, newPerPage);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchUsers(1, perPage);
    };

    const handleChangeStatus = async (user: AdminUser) => {
        const newStatus = user.status === 1 ? 0 : 1;
        const result = await Swal.fire({
            title: `¿${newStatus ? "Activar" : "Desactivar"} usuario?`,
            text: `¿Estás seguro de que deseas ${newStatus ? "activar" : "desactivar"} a ${user.firstName} ${user.lastName}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sí, cambiar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "var(--elPaket-primary-color, #e84d30)",
        });

        if (result.isConfirmed) {
            try {
                const response = await adminUserService.changeUserStatus({
                    idUser: user.id,
                    status: newStatus
                });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: "¡Actualizado!",
                        text: `El estado del usuario ha sido actualizado correctamente.`,
                        confirmButtonText: "Aceptar",
                        confirmButtonColor: "var(--elPaket-primary-color, #e84d30)",
                    });
                    fetchUsers(currentPage, perPage);
                } else {
                    errorService.showErrorAlert(response, "Error al cambiar estado");
                }
            } catch (err: any) {
                console.error("Error changing user status:", err);
                errorService.showErrorAlert(err, "Error de conexión");
            }
        }
    };

    const handleEdit = (user: AdminUser) => {
        setSelectedUser(user);
        setShowUpdateModal(true);
    };

    const columns: TableColumn<AdminUser>[] = [
        {
            name: "ID",
            selector: (row) => row.id,
            sortable: true,
            cell: (row) => (
                <div className={styles.orderNumber}>
                    {row.id}
                </div>
            ),
            width: "100px",
        },
        {
            name: "Nombre",
            selector: (row) => `${row.firstName} ${row.lastName}`,
            sortable: true,
            cell: (row) => (
                <div style={{ fontWeight: "600", color: "#1e293b" }}>
                    {row.firstName} {row.lastName}
                </div>
            ),
        },
        {
            name: "Email",
            selector: (row) => row.email,
            sortable: true,
        },
        {
            name: "Estado",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <div className={styles.switchContainer}>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            checked={row.status === 1}
                            disabled={!permissionCheckerService.checkPermission(adminData, "USERS", "CHANGE STATUS")}
                            onChange={() => handleChangeStatus(row)}
                        />
                        <span className={styles.slider}></span>
                    </label>
                </div>
            ),
            width: "120px",
            center: true,
        },
        {
            name: "Roles",
            cell: (row) => (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {row.roles && row.roles.length > 0 ? (
                        row.roles.map((role) => (
                            role.isSuperAdmin || (role as any).IsSuperAdmin ? (
                                <span
                                    key={role.id}
                                    style={{
                                        backgroundColor: "#7c3aed",
                                        color: "#ffffff",
                                        fontSize: "11px",
                                        fontWeight: "700",
                                        padding: "3px 8px",
                                        borderRadius: "12px",
                                        display: "inline-block",
                                    }}
                                >
                                    {role.roleName}
                                </span>
                            ) : (
                                <span key={role.id} className={styles.rolePill}>
                                    {role.roleName}
                                </span>
                            )
                        ))
                    ) : (
                        <span style={{ fontSize: "12px", color: "#94a3b8", fontStyle: "italic" }}>Sin roles</span>
                    )}
                </div>
            ),
        },
        {
            name: "Acciones",
            cell: (row) => (
                <div className={styles.actionsContainer}>
                    {permissionCheckerService.checkPermission(adminData, "USERS", "EDIT") && (
                        <button
                            className={styles.btnEdit}
                            onClick={() => handleEdit(row)}
                            title="Editar Usuario"
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                    )}
                </div>
            ),
            width: "100px",
            center: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--elPaket-primary-color)",
                color: "white",
                minHeight: "48px",
            },
        },
        headCells: {
            style: {
                color: "var(--brand-black)",
                fontWeight: "700",
                fontSize: "12px",
                textTransform: "uppercase" as any,
                letterSpacing: "0.5px",
            },
        },
        rows: {
            style: {
                minHeight: "70px",
                "&:hover": {
                    backgroundColor: "#f8fafc !important",
                },
            },
        },
    };

    return (
        <AdminDashboardLayout>
            <div className={styles.adminUsersContainer}>
                <DashboardCard
                    icon={faUsers}
                    title="Gestión de Usuarios"
                    description="Administra los usuarios del sistema, sus roles y estados de cuenta."
                    actions={
                        permissionCheckerService.checkPermission(adminData, "USERS", "CREATE") && (
                            <button
                                className={btnStyles.primary}
                                onClick={() => setShowCreateModal(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} /> Crear Nuevo Usuario
                            </button>
                        )
                    }
                />

                <div className={styles.tableWrapper}>
                    <form className={styles.tableActions} onSubmit={handleSearch}>
                        <div className={styles.filterSection}>
                            <div className={styles.filterGroup}>
                                <label>Estado</label>
                                <select
                                    className={styles.filterSelect}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="1">Activos</option>
                                    <option value="0">Inactivos</option>
                                </select>
                            </div>

                            <div className={styles.filterGroup}>
                                <label>Rol</label>
                                <select
                                    className={styles.filterSelect}
                                    value={roleFilter}
                                    onChange={(e) => setRoleFilter(e.target.value)}
                                >
                                    <option value="">Todos los roles</option>
                                    {roles.map((role) => (
                                        <option key={role.id} value={role.id}>
                                            {role.roleName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.searchRow}>
                            <div className={styles.searchWrapper}>
                                <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre o email..."
                                    className={styles.searchInput}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                            <button type="submit" className={btnStyles.primary}>
                                <FontAwesomeIcon icon={faSearch} /> Buscar
                            </button>
                        </div>
                    </form>

                    {error ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faTriangleExclamation} size="3x" style={{ color: "#ef4444", marginBottom: "16px" }} />
                            <h3>Error de Carga</h3>
                            <p>{error}</p>
                            <button className={btnStyles.primary} style={{ margin: "20px auto" }} onClick={() => fetchUsers(currentPage, perPage)}>
                                Reintentar
                            </button>
                        </div>
                    ) : users.length === 0 && !loading ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faUserSlash} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
                            <p>No se encontraron usuarios con los criterios de búsqueda.</p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={users}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationDefaultPage={currentPage}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            progressPending={loading}
                            progressComponent={
                                <div className={styles.loadingContainer}>
                                    <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={styles.loadingIcon} />
                                    <p>Cargando lista de usuarios...</p>
                                </div>
                            }
                            customStyles={customStyles}
                            responsive
                            highlightOnHover
                            noDataComponent={null}
                        />
                    )}
                </div>
            </div>

            <CreateUserModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onSuccess={() => fetchUsers(currentPage, perPage)}
                roles={roles}
            />

            <UpdateUserModal
                show={showUpdateModal}
                onHide={() => {
                    setShowUpdateModal(false);
                    setSelectedUser(null);
                }}
                onSuccess={() => fetchUsers(currentPage, perPage)}
                roles={roles}
                user={selectedUser}
            />

        </AdminDashboardLayout>
    );
};

export default AdminUsersPage;
